import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import NotificationPanel from './NotificationPanel';
import GuideToggle from './GuideToggle';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
import {
  LayoutDashboard, Users, GitCompare,
  Dumbbell, Swords, ClipboardList, CheckSquare, Target,
  Heart, ShieldAlert,
  Activity, Brain,
  GraduationCap, Route,
  Wallet, FileText, MessageSquare, UserCog, Lock, Flag,
  ChevronDown, ChevronRight, LogOut, PanelLeftClose, PanelLeft, Menu, X,
  Eye, User, Moon, Sun, Bell, BarChart3, ScrollText, KeyRound, ContactRound, Upload,
  Zap, Ruler, Images, Tag, CalendarRange, Sparkles, FileSpreadsheet, Building2, ClipboardCheck
} from 'lucide-react';

const Sidebar = ({ unreadCount = 0, onCollapseChange }) => {
  const { user, logout, hasModuleAccess } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    if (onCollapseChange) onCollapseChange(collapsed);
  }, [collapsed, onCollapseChange]);

  useEffect(() => {
    const groups = getNavigationGroups();
    const newOpen = {};
    groups.forEach(g => {
      const hasActive = g.items.some(i => isActive(i.path));
      if (hasActive) newOpen[g.id] = true;
    });
    // Open key labeled groups by default so important items are visible
    // without requiring the user to expand the group.
    const groupsToOpen = new Set(['today', 'athletes-teams']);
    groups.forEach(g => {
      if (g.label && groupsToOpen.has(g.id) && !newOpen[g.id]) {
        newOpen[g.id] = true;
      }
    });
    setOpenGroups(prev => ({ ...prev, ...newOpen }));
  }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const toggleGroup = (groupId) => {
    if (collapsed) return;
    setOpenGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const role = user?.role;

  const getNavigationGroups = () => {
    const groups = [];

    if (role === 'Player' || role === 'Parent') {
      const items = [];
      items.push({ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' });
      // Phase 8 — Player/Parent no longer see the Focus Hub. The new
      // Staff Work Center is Admin/Staff only by design.
      if (role === 'Parent') items.push({ id: 'parent-dashboard', label: 'Child Overview', icon: Eye, path: '/parent-dashboard' });
      items.push({ id: 'my-profile', label: 'My Profile', icon: User, path: '/my-profile' });
      if (hasModuleAccess(PM.TRAINING, 'view')) items.push({ id: 'my-training', label: 'Training', icon: Dumbbell, path: '/my-training' });
      if (hasModuleAccess(PM.MATCHES, 'view')) items.push({ id: 'my-performance', label: 'Performance', icon: Swords, path: '/my-performance' });
      if (hasModuleAccess(PM.MATCH_REPORTS, 'view')) items.push({ id: 'match-reports', label: 'Match Reports', icon: ClipboardList, path: '/match-reports' });
      if (hasModuleAccess(PM.FITNESS_ASSESSMENT, 'view')) items.push({ id: 'my-assessments', label: 'Fitness Assessments', icon: CheckSquare, path: '/my-assessments' });
      if (hasModuleAccess(PM.PDP, 'view')) items.push({ id: 'pdp', label: 'PDP', icon: Target, path: '/pdp' });
      if (hasModuleAccess(PM.WELLNESS, 'view')) items.push({ id: 'my-wellness', label: 'Wellness', icon: Heart, path: '/my-wellness' });
      items.push({ id: 'my-media', label: 'Media & Highlights', icon: Images, path: '/my-media' });
      if (role === 'Player') items.push({ id: 'mental-hub', label: 'Mental Tasks', icon: Brain, path: '/mental-hub' });
      items.push({ id: 'performance-programs', label: 'Training Programs', icon: Activity, path: '/performance-programs' });
      if (hasModuleAccess(PM.ACADEMIC, 'view')) items.push({ id: 'my-academic', label: 'Academic', icon: GraduationCap, path: '/my-academic' });
      if (hasModuleAccess(PM.CAREER_PATHWAY, 'view')) items.push({ id: 'career', label: 'Career Pathway', icon: Route, path: '/career-pathway' });
      if (hasModuleAccess(PM.FINANCE, 'view')) items.push({ id: 'my-finance', label: 'Finance', icon: Wallet, path: '/my-finance' });
      if (hasModuleAccess(PM.DOCUMENTS, 'view')) items.push({ id: 'my-documents', label: 'Documents', icon: FileText, path: '/my-documents' });
      if (hasModuleAccess(PM.ANNOUNCEMENTS, 'view')) items.push({ id: 'my-announcements', label: 'Communication', icon: MessageSquare, path: '/my-announcements', badge: unreadCount });
      if (role === 'Parent') items.push({ id: 'parent-insights', label: 'Parent Insights', icon: Brain, path: '/mental-hub' });
      groups.push({ id: 'main', label: null, items });
      return groups;
    }

    const isAdmin = role === 'Admin';
    const canView = (mod) => isAdmin || hasModuleAccess(mod, 'view');

    // ──────────────────────────────────────────────────────────────────
    // NAV TAXONOMY (Apr 2026 — reorganized for clarity, not changed logic).
    // Groups are ordered by descending frequency of daily use.
    // Route paths, permissions and item set unchanged — only labels/order.
    // ──────────────────────────────────────────────────────────────────

    // 1. TODAY — pinned daily entry points
    const today = [];
    if (canView(PM.DASHBOARD)) today.push({ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' });
    if (canView(PM.FOCUS_HUB)) today.push({ id: 'work-center', label: 'Work Center', icon: Zap, path: '/work-center' });
    if (today.length) groups.push({ id: 'today', label: 'Today', items: today });

    // 2. ATHLETES & TEAMS — individual + squad-identity context
    //    (Compare Athletes moved to Insights & Analytics — it's analytical)
    const ath = [];
    if (isAdmin) {
      ath.push({ id: 'athletes', label: 'Athletes', icon: Users, path: '/athletes' });
    } else if (canView(PM.ATHLETES)) {
      ath.push({ id: 'my-athletes', label: 'My Athletes', icon: Users, path: '/my-athletes' });
    }
    if (isAdmin || canView(PM.ATHLETES)) {
      ath.push({ id: 'visual-roster', label: 'Visual Roster', icon: Images, path: '/visual-roster' });
    }
    if (canView(PM.ATHLETE_TAGS)) ath.push({ id: 'athlete-tags', label: 'Athlete Tags', icon: Tag, path: '/athlete-tags' });
    if (canView(PM.TEAM_STATE)) ath.push({ id: 'team-state', label: 'Team State', icon: Users, path: '/team-state' });
    if (canView(PM.MEDIA_HIGHLIGHTS)) ath.push({ id: 'media', label: 'Media & Highlights', icon: Images, path: '/media' });
    if (ath.length) groups.push({ id: 'athletes-teams', label: 'Athletes & Teams', items: ath });

    // 3. PERFORMANCE — Do / Input workflows (operational delivery + alerting)
    //    Training Programs kept here alongside Training (scheduling structured
    //    programs = doing work, not analyzing).
    const perf = [];
    if (canView(PM.TRAINING)) perf.push({ id: 'training', label: 'Training', icon: Dumbbell, path: '/training' });
    if (canView(PM.TRAINING_PROGRAMS)) perf.push({ id: 'performance-programs', label: 'Training Programs', icon: Activity, path: '/performance-programs' });
    if (canView(PM.MATCHES)) perf.push({ id: 'matches', label: 'Matches', icon: Swords, path: '/matches' });
    if (canView(PM.MATCH_REPORTS)) perf.push({ id: 'match-reports', label: 'Match Reports', icon: ClipboardList, path: '/match-reports' });
    if (canView(PM.FITNESS_ASSESSMENT)) perf.push({ id: 'assessments', label: 'Fitness Assessments', icon: CheckSquare, path: '/assessments' });
    if (canView(PM.PDP)) perf.push({ id: 'pdp', label: 'PDP', icon: Target, path: '/pdp' });
    if (canView(PM.ALERTS_FLAGS)) perf.push({ id: 'alerts-flags', label: 'Alerts & Flags', icon: Flag, path: '/alerts-flags' });
    if (canView(PM.ALERT_RULES)) perf.push({ id: 'alert-config', label: 'Alert Rules', icon: Bell, path: '/alert-config' });
    if (perf.length) groups.push({ id: 'performance', label: 'Performance', items: perf });

    // 4. INSIGHTS & ANALYTICS — View / Analysis workflows (read-only)
    const insights = [];
    if (canView(PM.DECISION_ENGINE)) insights.push({ id: 'decision-engine', label: 'Decision Engine', icon: Brain, path: '/intelligence' });
    if (canView(PM.SQUAD_ANALYTICS)) insights.push({ id: 'squad-analytics', label: 'Squad Analytics', icon: BarChart3, path: '/analytics/squad' });
    if (isAdmin && canView(PM.ACADEMY_OVERVIEW)) insights.push({ id: 'academy-overview', label: 'Academy Overview', icon: Activity, path: '/analytics/academy' });
    if (canView(PM.SEASON_REPORTS)) insights.push({ id: 'season-reports', label: 'Season Reports', icon: FileText, path: '/season-reports' });
    // Phase 83 — Trial Reports live alongside Season Reports. Same
    // permission gate (reports module). Player/Parent only see entries
    // for athletes they can access; server-side filters out drafts.
    if (canView(PM.TRIAL_REPORTS)) insights.push({ id: 'trial-reports', label: 'Trial Reports', icon: FileText, path: '/trial-reports' });
    if (canView(PM.DEVELOPMENT_REPORTS)) insights.push({ id: 'development-reports', label: 'Player Development Reports', icon: ClipboardCheck, path: '/development-reports' });
    if (canView(PM.COMPARE_SEASONS)) insights.push({ id: 'season-compare', label: 'Compare Seasons', icon: BarChart3, path: '/season-reports/compare' });
    if (canView(PM.COMPARE_ATHLETES)) insights.push({ id: 'compare', label: 'Compare Athletes', icon: GitCompare, path: '/athletes/compare' });
    if (canView(PM.AI_ASSISTANT_VIEW)) insights.push({ id: 'pfap-ai-assistant', label: 'PFAP AI Assistant', icon: Sparkles, path: '/intelligence/ai-assistant' });
    if (insights.length) groups.push({ id: 'insights-analytics', label: 'Insights & Analytics', items: insights });

    // 5. PLAYER HEALTH — daily medical/mental care
    const health = [];
    if (canView(PM.DAILY_MONITORING)) health.push({ id: 'wellness-monitoring', label: 'Daily Monitoring', icon: Activity, path: '/wellness-monitoring' });
    if (canView(PM.INJURY_RECOVERY)) health.push({ id: 'injury-recovery', label: 'Injury & Recovery', icon: ShieldAlert, path: '/injury-recovery' });
    if (canView(PM.BIOMETRICS)) health.push({ id: 'biometrics', label: 'Biometrics', icon: Ruler, path: '/biometrics' });
    if (canView(PM.WELLNESS)) health.push({ id: 'wellness', label: 'Wellness Library', icon: Heart, path: '/wellness' });
    if (canView(PM.MENTAL_HUB)) health.push({ id: 'mental-hub', label: 'Mental Hub', icon: Brain, path: '/mental-hub' });
    if (health.length) groups.push({ id: 'player-health', label: 'Player Health', items: health });

    // 6. DEVELOPMENT — long-horizon growth
    const dev = [];
    if (canView(PM.ACADEMIC)) dev.push({ id: 'academic', label: 'Academic', icon: GraduationCap, path: '/academic' });
    if (canView(PM.CAREER_PATHWAY)) dev.push({ id: 'career', label: 'Career Pathway', icon: Route, path: '/career-pathway' });
    if (dev.length) groups.push({ id: 'development', label: 'Development', items: dev });

    // 7. OPERATIONS — shared operational tools (Admin + Staff with permissions)
    //    Seasons is permission-controlled here so staff can view current/past
    //    season metadata without inheriting any system-control privileges.
    const ops = [];
    if (canView(PM.FINANCE)) ops.push({ id: 'finance', label: 'Finance', icon: Wallet, path: '/finance' });
    if (canView(PM.DOCUMENTS)) ops.push({ id: 'documents', label: 'Documents', icon: FileText, path: '/documents' });
    if (canView(PM.ANNOUNCEMENTS)) ops.push({ id: 'announcements', label: 'Communication', icon: MessageSquare, path: '/announcements', badge: unreadCount });
    if (canView(PM.SEASONS)) ops.push({ id: 'seasons', label: 'Seasons', icon: CalendarRange, path: '/seasons' });
    if (ops.length) groups.push({ id: 'operations', label: 'Operations', items: ops });

    // 8. ADMIN-ONLY SYSTEM CONTROLS — never assignable as staff permissions.
    //    These pages must remain visible only to Admin role and the backend
    //    routes guard them with require_role(["Admin"]).
    const admin = [];
    if (isAdmin) {
      admin.push({ id: 'user-management', label: 'User Management', icon: UserCog, path: '/admin/users' });
      admin.push({ id: 'clubs', label: 'Club Management', icon: Building2, path: '/admin/clubs' });
      admin.push({ id: 'access-control', label: 'Access Control', icon: Lock, path: '/admin/access-control' });
      admin.push({ id: 'development-tags', label: 'Development Tags', icon: Tag, path: '/admin/development-tags' });
      admin.push({ id: 'manage-accounts', label: 'Manage Accounts', icon: ContactRound, path: '/admin/manage-accounts' });
      admin.push({ id: 'bulk-import', label: 'Bulk Import', icon: Upload, path: '/admin/bulk-import' });
      admin.push({ id: 'data-import', label: 'Data Import', icon: FileSpreadsheet, path: '/admin/data-import' });
      admin.push({ id: 'staff-credentials', label: 'Staff Credentials', icon: KeyRound, path: '/admin/staff-credentials' });
      admin.push({ id: 'system-logs', label: 'System Logs', icon: ScrollText, path: '/system-logs' });
    } else if (user?.role === 'Staff' && user?.can_view_own_logs) {
      // Staff with admin-granted log access see the page gated to their own entries only.
      admin.push({ id: 'system-logs', label: 'My Activity Logs', icon: ScrollText, path: '/system-logs' });
    }
    if (admin.length) groups.push({ id: 'administration', label: 'Administration', items: admin });

    return groups;
  };

  const navGroups = getNavigationGroups();

  const getRoleBadgeColor = (r) => {
    const map = { Admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', Staff: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', Player: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', Parent: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' };
    return map[r] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    return (
      <li key={item.id}>
        <button
          onClick={() => { navigate(item.path); setMobileOpen(false); }}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150 group relative ${
            active
              ? 'bg-pfap-green text-white dark:bg-gray-700 dark:text-white dark:border-l-2 dark:border-l-pfap-sage font-medium shadow-sm'
              : 'text-gray-600 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
          data-testid={`nav-${item.id}`}
          title={collapsed ? item.label : undefined}
        >
          <Icon size={18} className={active ? 'text-white' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300'} />
          {!collapsed && (
            <>
              <span className="flex-1 text-left text-sm truncate">{item.label}</span>
              {item.badge > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-medium rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </>
          )}
          {collapsed && item.badge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">
              {item.badge > 9 ? '9+' : item.badge}
            </span>
          )}
        </button>
      </li>
    );
  };

  return (
    <>
      <button
        className="fixed top-3 left-3 z-[60] p-2 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 lg:hidden shadow-md border border-gray-200 dark:border-gray-700"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 dark:bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`fixed left-0 top-0 h-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-all duration-200 z-50 flex flex-col border-r border-gray-200 dark:border-gray-700 shadow-sm ${
          collapsed ? 'w-[60px]' : 'w-60'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className={`flex items-center border-b border-gray-200 dark:border-gray-700 ${collapsed ? 'p-2 justify-center' : 'px-4 py-3 justify-between'}`}>
          {!collapsed ? (
            <div className="flex items-center gap-2.5 min-w-0">
              <img src={isDark ? "/pfap-logo-white.png" : "/pfap-logo-black.png"} alt="PFAP" className="h-8 w-8 flex-shrink-0 rounded-full" />
              <div className="min-w-0">
                <h1 className="text-base font-heading text-pfap-green dark:text-gray-100 leading-tight uppercase">PFAP Hub</h1>
                <p className="text-[9px] text-gray-400 dark:text-pfap-sage/60 leading-tight truncate font-body">Pro-Football Academy Portugal</p>
              </div>
            </div>
          ) : (
            <img src={isDark ? "/pfap-logo-white.png" : "/pfap-logo-black.png"} alt="PFAP" className="h-7 w-7 rounded-full" />
          )}
          <div className={`flex items-center gap-0.5 ${collapsed ? 'hidden' : ''}`}>
            <NotificationPanel />
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400 dark:text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex items-center justify-center"
              data-testid="theme-toggle"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400 dark:text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hidden lg:flex items-center justify-center"
              data-testid="sidebar-toggle"
            >
              {collapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
            </button>
          </div>
        </div>

        {/* User Info */}
        {!collapsed && (
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700/50">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-pfap-sage/30 text-pfap-green dark:bg-pfap-sage/20 dark:text-pfap-sage flex items-center justify-center text-sm font-semibold flex-shrink-0">
                {user?.full_name?.charAt(0) || '?'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate text-gray-800 dark:text-gray-200">{user?.full_name}</p>
                <Badge className={`text-[10px] px-1.5 py-0 ${getRoleBadgeColor(user?.role)}`}>{user?.role}</Badge>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2">
          {navGroups.map((group) => (
            <div key={group.id} className="mb-0.5">
              {group.label && !collapsed && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center justify-between px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 dark:text-gray-500 transition-colors"
                  data-testid={`nav-group-${group.id}`}
                >
                  <span>{group.label}</span>
                  {openGroups[group.id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </button>
              )}
              {collapsed && group.label && (
                <div className="border-t border-gray-100 dark:border-gray-700 mx-3 my-1" />
              )}
              {(collapsed || !group.label || openGroups[group.id]) && (
                <ul className={`space-y-0.5 ${collapsed ? 'px-1.5' : 'px-2'}`}>
                  {group.items.map(renderNavItem)}
                </ul>
              )}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className={`border-t border-gray-200 dark:border-gray-700 ${collapsed ? 'p-1.5' : 'p-3'}`}>
          {collapsed && (
            <>
              <button
                onClick={toggleTheme}
                className="w-full flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors mb-1"
                data-testid="theme-toggle-collapsed"
                title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <button
                onClick={() => setCollapsed(false)}
                className="w-full flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors mb-1"
              >
                <PanelLeft size={16} />
              </button>
            </>
          )}
          <GuideToggle collapsed={collapsed} />
          <Button
            variant="ghost"
            onClick={() => navigate('/change-password')}
            className={`w-full text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 h-9 ${collapsed ? 'px-0 justify-center' : ''}`}
            data-testid="change-password-button"
          >
            <KeyRound size={18} />
            {!collapsed && <span className="ml-2.5 text-sm">Change Password</span>}
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={`w-full text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 h-9 ${collapsed ? 'px-0 justify-center' : ''}`}
            data-testid="logout-button"
          >
            <LogOut size={18} />
            {!collapsed && <span className="ml-2.5 text-sm">Logout</span>}
          </Button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
